import React from 'react'
import { Button } from '@mui/material'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { DropzoneField, Form, FormRow } from '../formControls'
import useForm from '../../hooks/useForm'
import LoadingBtn from '../Button/LoadingButton'
import { fileMBSizes } from '../../services/utils'
import { useMediaLibrary } from './MediaLibraryContext'
import { isValidFileType } from './utils/helpers'
import DropzoneRejections from '../formControls/dropzone/DropzoneRejections'

export type UploadMediaFormFieldValues = {
	files: File[]
}

export type UploadMediaFormProps = {
	onSubmit?: (files: File[]) => void
	onCancel?: () => void
	renameParts?: string[]
}

const UploadMediaForm = ({
	onSubmit,
	onCancel = undefined,
	renameParts,
}: UploadMediaFormProps) => {
	const { formatMessage } = useIntl()

	const { droppedFiles, rejectedFiles, accept, clearRejectedFiles } =
		useMediaLibrary()

	const schema = yup.object().shape({
		files: yup
			.array()
			.min(1)
			.required(formatMessage({ id: 'upload_media.form.required' }))
			.test(
				'is-valid-type',
				formatMessage({ id: 'upload_media.form.is_valid_type' }),
				(files: File[]) => {
					return files.every((file) => isValidFileType(file, accept))
				}
			),
	})

	const form = useForm<UploadMediaFormFieldValues>({
		defaultValues: {
			files: droppedFiles ?? [],
		},
		values: {
			files: droppedFiles,
		},
		schema,
	})

	const {
		handleSubmit,
		formState: { errors, isSubmitting },
		control,
	} = form

	const handleFormSubmit = (data: UploadMediaFormFieldValues) => {
		onSubmit?.(data.files)
	}

	const helperText = `${formatMessage({ id: 'upload_media.form.accepted_file_types' })} ${Object.values(
		accept || {}
	)
		.map((value) => value.join(', '))
		.join(', ')}`

	const handleDropAccepted = () => {
		clearRejectedFiles()
	}

	return (
		<Form onSubmit={handleSubmit(handleFormSubmit)}>
			<FormRow>
				<DropzoneField
					name="files"
					accept={accept}
					helperText={helperText}
					control={control}
					error={!!rejectedFiles?.length}
					errors={errors}
					minSize={0}
					maxSize={fileMBSizes[20]}
					maxFiles={10}
					renameParts={renameParts}
					onDropAccepted={handleDropAccepted}
				/>
				{!!rejectedFiles?.length && (
					<DropzoneRejections
						fileRejections={rejectedFiles}
						maxSize={fileMBSizes[20]}
					/>
				)}
			</FormRow>
			<FormRow className="flex flex-row justify-between items-center mt-6">
				{onCancel && (
					<div className="mr-2">
						<Button id="cancel" onClick={onCancel} variant="outlined">
							{formatMessage({ id: 'remove_record.cancel' })}
						</Button>
					</div>
				)}
				<LoadingBtn
					disabled={isSubmitting}
					loading={isSubmitting}
					type="submit"
					text={formatMessage({ id: 'global.save' })}
					fullWidth={false}
				/>
			</FormRow>
		</Form>
	)
}

export default UploadMediaForm
